import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { repositoryApi } from "../api/repository";

export interface RepoContractor {
  id: string;
  name: string;
  short_name: string;
}

export function useContractorsList(enabled = true) {
  return useQuery({
    queryKey: ["contractors"],
    queryFn: () =>
      apiClient
        .get<{ data: RepoContractor[] }>("/contractors/")
        .then((r) => r.data.data),
    staleTime: 5 * 60_000,
    enabled,
  });
}

const keys = {
  categories: ["repository", "categories"] as const,
  list: (filters?: object) => ["repository", "list", filters ?? {}] as const,
  detail: (id: string) => ["repository", id] as const,
  versions: (id: string) => ["repository", id, "versions"] as const,
  access: (id: string) => ["repository", id, "access"] as const,
  contractorAccess: (id: string) => ["repository", id, "contractor-access"] as const,
  accessGroups: ["repository", "access-groups"] as const,
};

export function useResourceCategories() {
  return useQuery({
    queryKey: keys.categories,
    queryFn: repositoryApi.listCategories,
  });
}

export function useResources(filters?: { category?: string; type?: string }) {
  return useQuery({
    queryKey: keys.list(filters),
    queryFn: () => repositoryApi.list(filters),
    staleTime: 30_000,
  });
}

export function useResourceDetail(id: string | null) {
  return useQuery({
    queryKey: keys.detail(id!),
    queryFn: () => repositoryApi.get(id!),
    enabled: !!id,
    staleTime: 0,
  });
}

export function useResourceVersions(id: string | null) {
  return useQuery({
    queryKey: keys.versions(id!),
    queryFn: () => repositoryApi.listVersions(id!),
    enabled: !!id,
    staleTime: 0,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: repositoryApi.createCategory,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof repositoryApi.updateCategory>[1] }) =>
      repositoryApi.updateCategory(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: repositoryApi.deleteCategory,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}

export function useCreateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: repositoryApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repository", "list"] });
      qc.invalidateQueries({ queryKey: keys.categories });
    },
  });
}

export function useUpdateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof repositoryApi.update>[1] }) =>
      repositoryApi.update(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["repository", "list"] });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useSoftDeleteResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: repositoryApi.softDelete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repository", "list"] });
      qc.invalidateQueries({ queryKey: keys.categories });
    },
  });
}

export function useUploadVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file, comment }: { id: string; file: File; comment?: string }) =>
      repositoryApi.uploadVersion(id, file, comment),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["repository", "list"] });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.versions(id) });
    },
  });
}

export function useToggleSubscribe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, subscribed }: { id: string; subscribed: boolean }) =>
      subscribed ? repositoryApi.unsubscribe(id) : repositoryApi.subscribe(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repository", "list"] });
    },
  });
}

export function useUpdateAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof repositoryApi.updateAccess>[1] }) =>
      repositoryApi.updateAccess(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.access(id) });
    },
  });
}

export function useUpdateContractorAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { contractors: string[] } }) =>
      repositoryApi.updateContractorAccess(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.contractorAccess(id) });
    },
  });
}

// ---- Access groups (owner-private) ----

export function useAccessGroups(enabled = true) {
  // Mirrors backend can_create_access_groups: only directors/coordinators may list
  // access groups; for anyone else the request is a guaranteed 403, so skip it.
  const canManageGroups = useAuthStore(
    (s) => s.user?.role === "director" || s.user?.role === "coordinator",
  );
  return useQuery({
    queryKey: keys.accessGroups,
    queryFn: repositoryApi.listAccessGroups,
    staleTime: 30_000,
    enabled: enabled && canManageGroups,
  });
}

export function useCreateAccessGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: repositoryApi.createAccessGroup,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.accessGroups }),
  });
}

export function useUpdateAccessGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof repositoryApi.updateAccessGroup>[1] }) =>
      repositoryApi.updateAccessGroup(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.accessGroups }),
  });
}

export function useDeleteAccessGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: repositoryApi.deleteAccessGroup,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.accessGroups });
      // A deleted group also detaches from any resources it was attached to.
      qc.invalidateQueries({ queryKey: ["repository", "list"] });
    },
  });
}
